import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { useState, useEffect, useMemo } from 'react';
import { useInView } from '@/lib/useInView';
import { Phone, ChevronRight, ArrowRight, MapPin, Mail, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { CategoryBuyingGuide } from '@/components/CategoryBuyingGuide';
import { POGRZEB_BUYING_GUIDE } from '@/data/remainingP1SeoContent';
import { useBreakpoint, useIsNarrowPhone } from '@/hooks/use-mobile';
import { fetchCollection, productDisplayPrice, type ShopifyCollection, type ShopifyProduct } from '@/lib/shopifyApi';
import { CollectionProductCard } from '@/components/collection/CollectionProductCard';
import {
  CollectionFilters,
  type FilterState,
  EMPTY_FILTERS,
  hasActiveFilters,
} from '@/components/collection/CollectionFilters';
import { POGRZEB_CONTENT } from '@/data/pogrzebContent';
import { collectionUrl } from '@/lib/collectionUrl';

/* ─── Design tokens ──────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#1a1209';
const FOREST = '#3d5c3a';   /* muted sage-forest — leitmotiv of this page */
const MUTED  = 'rgba(42,31,20,0.45)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const SLATE  = '#fff';   /* neutral section bg */

/* ─── Shared section header ──────────────────────────────────── */
function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, marginBottom: 20 }}>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: 0 }}>
        {text}
      </p>

    </div>
  );
}

/* ─── 1. HERO ────────────────────────────────────────────────── */
function Hero() {
  return (
    <section style={{
      position: 'relative', minHeight: 'calc(52dvh - 45px)',
      display: 'flex', alignItems: 'center',
      background: '#fff',
      overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
        <PageBreadcrumb crumbs={[{ label: 'Kompozycje pogrzebowe' }]} />
      </div>


      <div style={{
        position: 'relative', zIndex: 1, width: '100%', maxWidth: 1370,
        margin: '0 auto',
        padding: 'max(clamp(24px, 3vw, 48px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(24px, 3vw, 48px)',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>


          <p
            className="bk-fade-up bk-d4"
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: 400, color: MUTED, marginBottom: 16, fontStyle: 'italic' }}
          >
            <CmsEditorialText id="information.hero.tagline" value={POGRZEB_CONTENT.hero.tagline} section="Wstęp" label="Nadtytuł" />
          </p>

          <h1
            className="bk-fade-up bk-d5"
            style={{
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 'clamp(2.2rem, 5.9vw, 5.4rem)',
              fontWeight: 400, lineHeight: 1.0,
              color: DARK,
              margin: '0 0 28px',
            }}
          >
            <CmsEditorialText id="information.hero.headingLine1" value={POGRZEB_CONTENT.hero.headingLine1} section="Wstęp" label="Nagłówek — pierwsza linia" /><br />
            <em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.hero.headingAccent" value={POGRZEB_CONTENT.hero.headingAccent} section="Wstęp" label="Wyróżniona część nagłówka" /></em><br />
            <CmsEditorialText id="information.hero.headingLine2" value={POGRZEB_CONTENT.hero.headingLine2} section="Wstęp" label="Nagłówek — druga linia" />
          </h1>

          <p
            className="bk-fade-in bk-d6"
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: MUTED, lineHeight: 1.85, maxWidth: 540, margin: '0 auto 44px' }}
          >
            <CmsEditorialText id="information.hero.body" value={POGRZEB_CONTENT.hero.body} section="Wstęp" label="Opis" multiline />
          </p>

          <div
            className="bk-fade-up"
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', animationDelay: '0.75s' }}
          >
            <a
              href="tel:+48605030728"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                letterSpacing: '0.18em', textTransform: 'uppercase',
                color: '#fff', textDecoration: 'none',
                background: FOREST,
                padding: '15px 32px', transition: 'opacity 0.2s', cursor: 'pointer',
              }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.85')}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
            >
              <Phone size={13} />
              <CmsEditorialText id="information.hero.ctaCallLabel" value={POGRZEB_CONTENT.hero.ctaCallLabel} section="Wstęp" label="Tekst przycisku" />
            </a>
            <a
              href="#kompozycje"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                letterSpacing: '0.18em', textTransform: 'uppercase',
                color: DARK, textDecoration: 'none',
                border: `1px solid ${GOLD}88`,
                padding: '15px 32px', transition: 'border-color 0.2s', cursor: 'pointer',
              }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD)}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = `${GOLD}88`)}
            >
              <CmsEditorialText id="information.hero.ctaSeeLabel" value={POGRZEB_CONTENT.hero.ctaSeeLabel} section="Wstęp" label="Tekst przycisku" />
              <ChevronRight size={13} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

import { usePrerenderReady } from '@/hooks/usePrerenderReady';

/* ─── Product grid constants ─────────────────────────────────── */
const FUNERAL_KOMPOZYCJE = [
  { value: 'Bukiety pogrzebowe',  label: 'Bukiety pogrzebowe',  emoji: '💐', handle: 'bukiety-pogrzebowe'  },
  { value: 'Wieńce pogrzebowe',   label: 'Wieńce pogrzebowe',   emoji: '🌿', handle: 'wience-pogrzebowe'   },
  { value: 'Wiązanki pogrzebowe', label: 'Wiązanki pogrzebowe', emoji: '🌸', handle: 'wiazanki-pogrzebowe' },
  { value: 'Sztuczne kompozycje', label: 'Sztuczne kompozycje', emoji: '🌺', handle: 'sztuczne-kompozycje' },
] as const;

const SORT_OPTIONS = [
  { label: 'Polecane',          value: 'COLLECTION_DEFAULT', reverse: false },
  { label: 'Najpopularniejsze', value: 'BEST_SELLING',       reverse: false },
  { label: 'Cena ↑',            value: 'PRICE',              reverse: false },
  { label: 'Cena ↓',            value: 'PRICE',              reverse: true  },
  { label: 'Najnowsze',         value: 'CREATED',            reverse: true  },
];

/* ─── Sort dropdown ──────────────────────────────────────────── */
function SortDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(o => `${o.value}:${o.reverse}` === value) ?? SORT_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} onMouseDown={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '10px 20px', minHeight: 44,
          border: `1px solid ${open ? DARK : 'rgba(42,31,20,0.18)'}`,
          background: open ? DARK : '#fff',
          fontFamily: 'DM Sans, sans-serif', fontSize: 10,
          letterSpacing: '0.13em', textTransform: 'uppercase',
          fontWeight: 600, color: open ? '#fff' : 'rgba(42,31,20,0.6)',
          cursor: 'pointer', transition: 'all 0.18s',
        }}
      >
        <SlidersHorizontal size={13} color={open ? 'rgba(255,255,255,0.7)' : GOLD} />
        {current.label}
        <ChevronDown size={11} color={open ? 'rgba(255,255,255,0.7)' : 'rgba(42,31,20,0.35)'}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 30,
          background: '#fff', border: `1px solid ${LINE}`,
          minWidth: 200, boxShadow: '0 12px 40px rgba(42,31,20,0.1)',
        }}>
          {SORT_OPTIONS.map(o => {
            const key = `${o.value}:${o.reverse}`;
            const active = value === key;
            return (
              <button key={key} onClick={() => { onChange(key); setOpen(false); }} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px',
                background: active ? 'rgba(210,60,120,0.07)' : 'transparent',
                border: 'none', cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                color: active ? DARK : 'rgba(42,31,20,0.7)', fontWeight: active ? 600 : 400,
                borderLeft: active ? `2px solid ${GOLD}` : '2px solid transparent',
              }}>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Skeleton grid ──────────────────────────────────────────── */
function SkeletonGrid() {
  return (
    <div className="funeral-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i}>
          <div style={{ aspectRatio: '4/5', background: 'rgba(210,60,120,0.07)', marginBottom: 0 }} className="animate-pulse" />
          <div style={{ padding: '16px 18px 20px', background: '#fff' }}>
            <div style={{ height: 18, background: 'rgba(210,60,120,0.07)', marginBottom: 8, width: '70%' }} className="animate-pulse" />
            <div style={{ height: 13, background: 'rgba(210,60,120,0.07)', marginBottom: 12, width: '50%' }} className="animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Tagged product type ────────────────────────────────────── */
type TaggedProduct = ShopifyProduct & { _category: string };

/* ─── Product grid — category via Rodzaj kompozycji filter ───── */
function FuneralProductsSection() {
  const [sort, setSort] = useState('COLLECTION_DEFAULT:false');
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [allTagged, setAllTagged] = useState<TaggedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  usePrerenderReady(!loading);
  const { ref: hdgRef, inView: hdgInView } = useInView<HTMLDivElement>();

  /* Fetch all 4 collections in parallel whenever sort changes */
  useEffect(() => {
    setLoading(true);
    const [sortKey, reverseStr] = sort.split(':');
    Promise.all(
      FUNERAL_KOMPOZYCJE.map(col =>
        fetchCollection(col.handle, { sort: sortKey, reverse: reverseStr === 'true', limit: 100 })
          .then(data => data
            ? {
                category: col.value,
                products: data.products.nodes.map(p => ({ ...p, tags: p.tags ?? [], _category: col.value })),
              }
            : { category: col.value, products: [] as TaggedProduct[] }
          )
          .catch(err => {
            // Preserve this category from the previous complete/partial
            // result; another category can still refresh successfully.
            console.warn(`[FuneralProductsSection] refresh failed for ${col.handle}:`, err);
            return null;
          })
      )
    )
      .then(results => {
        setAllTagged(previous => {
          const byCategory = new Map<string, TaggedProduct[]>();
          for (const product of previous) {
            const current = byCategory.get(product._category) ?? [];
            current.push(product);
            byCategory.set(product._category, current);
          }
          for (const result of results) {
            if (!result) continue;
            byCategory.set(result.category, result.products);
          }
          const uniqueProducts = new Map<string, TaggedProduct>();
          for (const product of FUNERAL_KOMPOZYCJE.flatMap(col => byCategory.get(col.value) ?? [])) {
            const productKey = product.handle || product.id;
            if (!uniqueProducts.has(productKey)) uniqueProducts.set(productKey, product);
          }
          return [...uniqueProducts.values()];
        });
      })
      .finally(() => setLoading(false));
  }, [sort]);

  const maxPrice = useMemo(() => {
    if (!allTagged.length) return 1000;
    return Math.ceil(Math.max(...allTagged.map(p => parseFloat(p.priceRange.minVariantPrice.amount))) / 50) * 50;
  }, [allTagged]);

  useEffect(() => {
    if (maxPrice > 0) setFilters(f => ({ ...f, priceRange: [f.priceRange[0], Math.max(f.priceRange[1], maxPrice)] }));
  }, [maxPrice]);

  const filtered = useMemo(() =>
    allTagged.filter(p => {
      if (filters.kompozycje.length > 0 && !filters.kompozycje.includes(p._category)) return false;
      const price = parseFloat(p.priceRange.minVariantPrice.amount);
      return price >= filters.priceRange[0] && price <= filters.priceRange[1];
    }),
  [allTagged, filters]);

  const activeFilters = hasActiveFilters(filters, maxPrice);

  return (
    <section id="kompozycje" style={{ background: '#ffffff', paddingTop: 'clamp(56px,8vw,112px)' }}>

      {/* ── Heading ── */}
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)', paddingBottom: 'clamp(40px,5vw,64px)' }}>
        <div ref={hdgRef} style={{ opacity: hdgInView ? undefined : 0, textAlign: 'center', marginBottom: 'clamp(40px,5vw,64px)' }} className={hdgInView ? 'bk-fade-up bk-d1' : undefined}>
          <SectionLabel text={POGRZEB_CONTENT.products.eyebrow} />
          <h2 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(1.9rem,4vw,3.2rem)', fontWeight: 400,
            lineHeight: 1.1, color: DARK, margin: '0 0 16px',
          }}>
            <CmsEditorialText id="information.products.heading" value={POGRZEB_CONTENT.products.heading} section="Wprowadzenie do oferty" label="Nagłówek" />
          </h2>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: MUTED,
            maxWidth: 500, margin: '0 auto', lineHeight: 1.75,
          }}>
            <CmsEditorialText id="information.products.body" value={POGRZEB_CONTENT.products.body} section="Wprowadzenie do oferty" label="Opis" multiline />
          </p>
        </div>
      </div>

      {/* ── Sticky filter bar — identyczny jak strony kategorii ── */}
      {!loading && allTagged.length > 0 && (
        <div style={{
          background: '#ffffff',
          borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`,
        }}>
          <div style={{ padding: '14px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CollectionFilters
                  filters={filters}
                  onChange={setFilters}
                  maxPrice={maxPrice}
                  sortValue={sort}
                  onSortChange={setSort}
                  sortOptions={SORT_OPTIONS}
                  visibleSections={['kompozycje', 'cena']}
                  kompozycjeOptions={FUNERAL_KOMPOZYCJE.map(({ value, label, emoji }) => ({ value, label, emoji }))}
                  kompozycjeLabel="Rodzaj kompozycji"
                />
              </div>
              <SortDropdown value={sort} onChange={setSort} />
            </div>
          </div>
        </div>
      )}

      {/* ── Products ── */}
      <div style={{
        padding: 'clamp(40px,5vw,72px) 0 clamp(64px,8vw,112px)',
      }}>
        <style>{`
          .funeral-grid { grid-template-columns: repeat(4,1fr); }
          @media (max-width: 768px)  { .funeral-grid { grid-template-columns: repeat(2,1fr) !important; } }
        `}</style>

        {loading && <SkeletonGrid />}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, color: DARK, marginBottom: 8 }}>
              {activeFilters ? 'Brak produktów spełniających filtry' : 'Brak produktów'}
            </p>
            {activeFilters && (
              <button
                onClick={() => setFilters({ ...EMPTY_FILTERS, priceRange: [0, maxPrice] })}
                style={{
                  fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                  color: GOLD, background: 'none', border: 'none', cursor: 'pointer',
                  textTransform: 'uppercase', textDecoration: 'underline',
                }}
              >
                Wyczyść filtry
              </button>
            )}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, marginBottom: 20, letterSpacing: '0.04em' }}>
              {filtered.length} {filtered.length === 1 ? 'produkt' : filtered.length < 5 ? 'produkty' : 'produktów'}
            </p>
            <div className="funeral-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
              {filtered.map((product, i) => (
                <CollectionProductCard
                  key={`${product.handle}-${product._category}`}
                  product={product}
                  price={productDisplayPrice(product.priceRange)}
                  index={i}
                  collectionCtx={{ label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' }}
                  centerInfo
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ─── 3. SERVICES ────────────────────────────────────────────── */
function ServicesSection() {
  const bp = useBreakpoint();
  const narrow = useIsNarrowPhone();
  const cols = bp === 'desktop' ? 'repeat(3,1fr)' : narrow ? '1fr' : 'repeat(2,1fr)';
  const { ref, inView } = useInView<HTMLElement>();

  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(56px,8vw,112px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ opacity: inView ? undefined : 0, textAlign: 'center', marginBottom: 'clamp(40px,5vw,64px)' }} className={inView ? 'bk-fade-up bk-d1' : undefined}>
          <SectionLabel text={POGRZEB_CONTENT.services.eyebrow} />
          <h2 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(1.9rem,4vw,3.2rem)', fontWeight: 400,
            lineHeight: 1.1, color: DARK, margin: '0 0 16px',
          }}>
            <CmsEditorialText id="information.services.heading" value={POGRZEB_CONTENT.services.heading} section="Usługi" label="Nagłówek" />
          </h2>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: MUTED,
            maxWidth: 500, margin: '0 auto', lineHeight: 1.75,
          }}>
            <CmsEditorialText id="information.services.body" value={POGRZEB_CONTENT.services.body} section="Usługi" label="Opis" multiline />
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 1, background: LINE }}>
          {POGRZEB_CONTENT.services.items.map((s, i) => (
            <div
              key={s.title}
              style={{
                background: '#fff', padding: 'clamp(24px,2.5vw,36px)',
                borderTop: `2px solid ${GOLD}`,
                display: 'flex', flexDirection: 'column', gap: 12,
                animationDelay: `${i * 0.07}s`,
                opacity: inView ? undefined : 0,
              }}
              className={inView ? 'bk-fade-up' : undefined}
            >
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 12, color: GOLD, letterSpacing: '0.1em' }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(16px,1.8vw,21px)', fontWeight: 400,
                color: DARK, margin: 0, lineHeight: 1.2,
              }}>
                <CmsEditorialText id={`information.services.items.${i}.title`} value={s.title} section="Usługi" label={`Element ${i + 1}: tytuł`} />
              </h3>
              <p style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                color: MUTED, lineHeight: 1.72, margin: 0, flexGrow: 1,
              }}>
                <CmsEditorialText id={`information.services.items.${i}.desc`} value={s.desc} section="Usługi" label={`Element ${i + 1}: opis`} multiline />
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 4. PROCESS ─────────────────────────────────────────────── */
function ProcessSection() {
  const bp = useBreakpoint();
  const narrow = useIsNarrowPhone();
  const isDesktop = bp === 'desktop';
  const cols = isDesktop ? 'repeat(4,1fr)' : narrow ? '1fr' : 'repeat(2,1fr)';

  const { ref, inView } = useInView<HTMLElement>('-80px 0px');

  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(56px,8vw,112px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>

        {/* Heading */}
        <div
          style={{ textAlign: 'center', marginBottom: 'clamp(40px,5vw,64px)', opacity: inView ? undefined : 0 }}
          className={inView ? 'bk-fade-up' : undefined}
        >
          <SectionLabel text={POGRZEB_CONTENT.process.eyebrow} />
          <h2 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(1.9rem,4vw,3.2rem)', fontWeight: 400,
            color: DARK, margin: 0, lineHeight: 1.1,
          }}>
            <CmsEditorialText id="information.process.heading" value={POGRZEB_CONTENT.process.heading} section="Jak to działa" label="Nagłówek" />
          </h2>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: cols,
          gap: isDesktop ? 0 : 'clamp(24px,4vw,40px)',
          position: 'relative',
        }}>
          {/* Connecting line — animated, desktop only */}

          {POGRZEB_CONTENT.process.steps.map((p, i) => (
            <div
              key={p.num}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: isDesktop ? 'clamp(20px,2vw,32px) clamp(12px,2.5vw,28px)' : '0',
                textAlign: 'center', position: 'relative', zIndex: 1,
                opacity: inView ? undefined : 0,
                animationDelay: `${0.2 + i * 0.12}s`,
              }}
              className={inView ? 'bk-fade-up' : undefined}
            >
              {/* Icon circle — hover effect */}
              <div
                style={{
                  width: 76, height: 76, borderRadius: '50%',
                  border: `1.5px solid ${GOLD}`,
                  background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 30,
                  marginBottom: 20,
                  boxShadow: `0 4px 20px rgba(210,60,120,0.22)`,
                  position: 'relative',
                  cursor: 'default',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.13)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(210,60,120,0.48)';
                  const span = e.currentTarget.querySelector('.bk-step-emoji') as HTMLElement | null;
                  if (span) span.style.transform = 'rotate(14deg) scale(1.18)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(210,60,120,0.22)';
                  const span = e.currentTarget.querySelector('.bk-step-emoji') as HTMLElement | null;
                  if (span) span.style.transform = '';
                }}
              >
                <span
                  className="bk-step-emoji"
                  style={{ display: 'inline-block', lineHeight: 1, transition: 'transform 0.25s ease' }}
                >
                  {p.emoji}
                </span>

                {/* Step number badge */}
                <span aria-hidden style={{
                  position: 'absolute', top: -6, right: -6,
                  width: 22, height: 22, borderRadius: '50%',
                  background: GOLD,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 9, fontWeight: 600, color: '#fff',
                  letterSpacing: '0.02em',
                }}>
                  {i + 1}
                </span>
              </div>

              <span aria-hidden style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                letterSpacing: '0.25em', color: GOLD,
                textTransform: 'uppercase', marginBottom: 10,
                fontWeight: 500, display: 'block',
              }}>
                Krok {p.num}
              </span>
              <h3 style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(1.1rem,1.5vw,1.35rem)', fontWeight: 600,
                color: DARK, margin: '0 0 12px', lineHeight: 1.25,
              }}>
                <CmsEditorialText id={`information.process.steps.${i}.title`} value={p.title} section="Jak to działa" label={`Element ${i + 1}: tytuł`} />
              </h3>
              <p style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 'clamp(12.5px,1.1vw,14px)', fontWeight: 400,
                color: MUTED, lineHeight: 1.65, margin: 0,
                maxWidth: '22ch', marginInline: 'auto',
              }}>
                <CmsEditorialText id={`information.process.steps.${i}.desc`} value={p.desc} section="Jak to działa" label={`Element ${i + 1}: opis`} multiline />
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 5. ADVISORY ────────────────────────────────────────────── */
const CONTACT_ROW_ICON_MAP: Record<string, React.ReactNode> = {
  mapPin: <MapPin size={13} color={GOLD} />,
  mail: <Mail size={13} color={GOLD} />,
  phone: <Phone size={13} color={GOLD} />,
};

function AdvisorySection() {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const { ref, inView } = useInView<HTMLElement>();

  return (
    <section ref={ref} style={{
      background: '#fff', padding: 'clamp(56px,8vw,112px) 0',
      position: 'relative', overflow: 'hidden',
    }}>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 36 : 'clamp(40px,6vw,96px)',
          alignItems: 'center',
        }}>
          {/* Left: quote */}
          <div style={{ opacity: inView ? undefined : 0 }} className={inView ? 'bk-fade-up bk-d1' : undefined}>
            <p style={{
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 'clamp(1rem,2.2vw,1.55rem)', fontStyle: 'italic',
              fontWeight: 400, color: DARK, lineHeight: 1.75, margin: '0 0 28px',
            }}>
              <CmsEditorialText id="information.advisory.quote" value={POGRZEB_CONTENT.advisory.quote} section="Kontakt i porady" label="Cytat" multiline />
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.14em', color: GOLD }}>
                <CmsEditorialText id="information.advisory.quoteAttribution" value={POGRZEB_CONTENT.advisory.quoteAttribution} section="Kontakt i porady" label="Podpis cytatu" multiline />
              </span>
            </div>
          </div>

          {/* Right: contact card */}
          <div
            style={{ border: `1px solid ${GOLD}66`, padding: isMobile ? 'clamp(24px,6vw,36px)' : 'clamp(32px,4vw,52px)', background: '#fff', animationDelay: '0.15s', opacity: inView ? undefined : 0 }}
            className={inView ? 'bk-fade-up' : undefined}
          >
            <SectionLabel text={POGRZEB_CONTENT.advisory.contactEyebrow} />
            <h2 style={{
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 'clamp(1.7rem,3vw,2.8rem)', fontWeight: 400,
              color: DARK, margin: '0 0 20px', lineHeight: 1.1,
            }}>
              <CmsEditorialText id="information.advisory.contactHeadingLine1" value={POGRZEB_CONTENT.advisory.contactHeadingLine1} section="Kontakt i porady" label="Nagłówek kontaktu — pierwsza linia" /><br /><CmsEditorialText id="information.advisory.contactHeadingLine2" value={POGRZEB_CONTENT.advisory.contactHeadingLine2} section="Kontakt i porady" label="Nagłówek kontaktu — druga linia" />
            </h2>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 14,
              color: MUTED, lineHeight: 1.8, marginBottom: 32,
            }}>
              <CmsEditorialText id="information.advisory.contactBody" value={POGRZEB_CONTENT.advisory.contactBody} section="Kontakt i porady" label="Opis kontaktu" multiline />
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, borderTop: `1px solid ${GOLD}44` }}>
              {POGRZEB_CONTENT.advisory.contactRows.map(row => (
                <a
                  key={row.label}
                  href={row.href}
                  target={row.href.startsWith('http') ? '_blank' : undefined}
                  rel={row.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '16px 0', borderBottom: `1px solid ${GOLD}44`,
                    textDecoration: 'none', transition: 'opacity 0.2s', cursor: 'pointer',
                  }}
                  onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.7')}
                  onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    border: `1px solid ${GOLD}66`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {CONTACT_ROW_ICON_MAP[row.iconKey]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontFamily: 'DM Sans, sans-serif', fontSize: 9,
                      letterSpacing: '0.22em', textTransform: 'uppercase',
                      color: GOLD, margin: '0 0 4px',
                    }}>
                      {row.label}
                    </p>
                    <p style={{
                      fontFamily: 'Cormorant Garamond, serif',
                      fontSize: isMobile ? 17 : 20, fontWeight: 400,
                      color: DARK, margin: 0, lineHeight: 1.2,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {row.value}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            <div style={{ marginTop: 28 }}>
              <a
                href="tel:+48605030728"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10, width: '100%',
                  justifyContent: 'center',
                  fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: '#fff', textDecoration: 'none',
                  background: FOREST, padding: '14px 28px',
                  transition: 'opacity 0.2s', cursor: 'pointer',
                }}
                onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.85')}
                onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
              >
                <Phone size={13} />
                <CmsEditorialText id="information.advisory.ctaLabel" value={POGRZEB_CONTENT.advisory.ctaLabel} section="Kontakt i porady" label="Tekst przycisku" />
                <ArrowRight size={13} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function PogrzebPage() {
  useSeo(PAGE_SEO.pogrzeb);
  return (
    <div className="min-h-[100dvh]" style={{ background: '#fff' }}>
      <main>
        <Hero />
        <FuneralProductsSection />
        <nav
          aria-label="Rodzaje kompozycji pogrzebowych"
          style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter) clamp(40px,5vw,64px)' }}
        >
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: MUTED, margin: '0 0 14px' }}>
            Rodzaje kompozycji
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {FUNERAL_KOMPOZYCJE.map(item => (
              <a
                key={item.handle}
                href={collectionUrl(item.handle)}
                style={{ color: DARK, textDecoration: 'none', border: `1px solid ${LINE}`, padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: 13 }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </nav>
        <ServicesSection />
        <ProcessSection />
        <AdvisorySection />
      </main>
      <CategoryBuyingGuide content={POGRZEB_BUYING_GUIDE} />
      <Footer />
    </div>
  );
}
